import { PLATFORM_ADMIN_API, PLATFORM_API, adminEnterpriseProvisioningReadSchema, adminEnterpriseProvisioningSchema, organizationCreatedSchema, actionAuthorizeResponseSchema, adminDomainListSchema, adminDomainSchema, auditPageSchema, adminHealthSchema, adminKeySchema, adminKeysSchema, adminKeysSummarySchema, adminProductSubscriptionListSchema, adminProductSubscriptionSchema, auth0OrganizationLinkSchema, staffMemberListSchema, adminCustomerAccountListSchema, adminCustomerAccountSchema, adminPortalListSchema, adminPortalSchema, staffMemberSchema, staffInvitationListSchema, staffInvitationSchema, domainCreatedSchema, effectiveModuleSchema, emergencyStopListSchema, emergencyStopSchema, entitlementListSchema, entitlementSchema, invitationListSchema, invitationSummarySchema, memberListSchema, memberSchema, moduleCatalogSchema, moduleConfigurationResultSchema, organizationModulesSchema, releaseFlagListSchema, releaseFlagSchema, supportAccessGrantListSchema, supportAccessGrantSchema, } from '@traceio-org/platform-contracts';
/** Route templates for error reports (PLATFORM_ADMIN_API builds the paths). */
const ORGANIZATION = '/v1/admin/organizations/:organizationId';
const ROUTES = {
    organizationModules: `${ORGANIZATION}/modules`,
    organizationModule: `${ORGANIZATION}/modules/:moduleKey`,
    organizationEntitlements: `${ORGANIZATION}/entitlements`,
    organizationEmergencyStops: `${ORGANIZATION}/emergency-stops`,
    releaseEmergencyStop: `${ORGANIZATION}/emergency-stops/:stopId/release`,
    members: `${ORGANIZATION}/members`,
    member: `${ORGANIZATION}/members/:membershipId`,
    invitations: `${ORGANIZATION}/invitations`,
    revokeInvitation: `${ORGANIZATION}/invitations/:invitationId/revoke`,
    domains: `${ORGANIZATION}/domains`,
    domainAction: `${ORGANIZATION}/domains/:domainId/actions`,
    supportAccessRevoke: '/v1/admin/support-access/:grantId/revoke',
    organizationAuth0: `${ORGANIZATION}/auth0-organization`,
    subscriptions: `${ORGANIZATION}/subscriptions`,
    endSubscription: `${ORGANIZATION}/subscriptions/:subscriptionId/end`,
    staffMember: '/v1/admin/staff/:userId',
    revokeStaffInvitation: '/v1/admin/staff/invitations/:invitationId/revoke',
    keyExpiry: '/v1/admin/keys/:project/:name/expiry',
    keyProvider: '/v1/admin/keys/:project/:name/provider',
    keyTracking: '/v1/admin/keys/:project/:name/tracking',
    portals: `${ORGANIZATION}/portals`,
    portal: `${ORGANIZATION}/portals/:portalId`,
    portalInvitations: `${ORGANIZATION}/portals/:portalId/invitations`,
    customerAccounts: `${ORGANIZATION}/customer-accounts`,
    customerAccount: `${ORGANIZATION}/customer-accounts/:accountId`,
    enterpriseProvisioning: `${ORGANIZATION}/enterprise-provisioning`,
    retryEnterpriseProvisioning: `${ORGANIZATION}/enterprise-provisioning/retry`,
};
export function createAdminMethods(request) {
    // Every route here is authenticated: the token of the call (RequestOptions.accessToken) or getAccessToken.
    const call = (spec, options) => request({ ...spec, auth: 'bearer', signal: options?.signal, accessToken: options?.accessToken });
    return {
        adminModules: (options) => call({
            route: PLATFORM_ADMIN_API.modules,
            path: PLATFORM_ADMIN_API.modules,
            method: 'GET',
            schema: moduleCatalogSchema,
        }, options),
        adminOrganizationModules: (organizationId, options) => call({
            route: ROUTES.organizationModules,
            path: PLATFORM_ADMIN_API.organizationModules(organizationId),
            method: 'GET',
            schema: organizationModulesSchema,
        }, options),
        adminOrganizationModule: (organizationId, moduleKey, options) => call({
            route: ROUTES.organizationModule,
            path: PLATFORM_ADMIN_API.organizationModule(organizationId, moduleKey),
            method: 'GET',
            schema: effectiveModuleSchema,
        }, options),
        adminUpdateOrganizationModule: (organizationId, moduleKey, change, options) => call({
            route: ROUTES.organizationModule,
            path: PLATFORM_ADMIN_API.organizationModule(organizationId, moduleKey),
            method: 'PATCH',
            body: change,
            schema: moduleConfigurationResultSchema,
        }, options),
        adminOrganizationEntitlements: (organizationId, options) => call({
            route: ROUTES.organizationEntitlements,
            path: PLATFORM_ADMIN_API.organizationEntitlements(organizationId),
            method: 'GET',
            schema: entitlementListSchema,
        }, options),
        adminGrantEntitlement: (organizationId, body, options) => call({
            route: ROUTES.organizationEntitlements,
            path: PLATFORM_ADMIN_API.organizationEntitlements(organizationId),
            method: 'POST',
            body,
            schema: entitlementSchema,
        }, options),
        adminEmergencyStops: (organizationId, options) => call({
            route: ROUTES.organizationEmergencyStops,
            path: PLATFORM_ADMIN_API.organizationEmergencyStops(organizationId),
            method: 'GET',
            schema: emergencyStopListSchema,
        }, options),
        adminStartEmergencyStop: (organizationId, body, options) => call({
            route: ROUTES.organizationEmergencyStops,
            path: PLATFORM_ADMIN_API.organizationEmergencyStops(organizationId),
            method: 'POST',
            body,
            schema: emergencyStopSchema,
        }, options),
        adminReleaseEmergencyStop: (organizationId, stopId, body, options) => call({
            route: ROUTES.releaseEmergencyStop,
            path: PLATFORM_ADMIN_API.releaseEmergencyStop(organizationId, stopId),
            method: 'POST',
            body,
            schema: emergencyStopSchema,
        }, options),
        adminReleaseFlags: (options) => call({
            route: PLATFORM_ADMIN_API.releaseFlags,
            path: PLATFORM_ADMIN_API.releaseFlags,
            method: 'GET',
            schema: releaseFlagListSchema,
        }, options),
        adminSetReleaseFlag: (body, options) => call({
            route: PLATFORM_ADMIN_API.releaseFlags,
            path: PLATFORM_ADMIN_API.releaseFlags,
            method: 'PUT',
            body,
            schema: releaseFlagSchema,
        }, options),
        adminMembers: (organizationId, query, options) => call({
            route: ROUTES.members,
            path: PLATFORM_ADMIN_API.members(organizationId),
            method: 'GET',
            query: {
                query: query?.query,
                status: query?.status,
                productKey: query?.productKey,
                cursor: query?.cursor,
                limit: query?.limit,
            },
            schema: memberListSchema,
        }, options),
        adminMember: (organizationId, membershipId, options) => call({
            route: ROUTES.member,
            path: PLATFORM_ADMIN_API.member(organizationId, membershipId),
            method: 'GET',
            schema: memberSchema,
        }, options),
        adminUpdateMember: (organizationId, membershipId, body, options) => call({
            route: ROUTES.member,
            path: PLATFORM_ADMIN_API.member(organizationId, membershipId),
            method: 'PATCH',
            body,
            schema: memberSchema,
        }, options),
        adminInvitations: (organizationId, options) => call({
            route: ROUTES.invitations,
            path: PLATFORM_ADMIN_API.invitations(organizationId),
            method: 'GET',
            schema: invitationListSchema,
        }, options),
        adminCreateInvitation: (organizationId, body, options) => call({
            route: ROUTES.invitations,
            path: PLATFORM_ADMIN_API.invitations(organizationId),
            method: 'POST',
            body,
            schema: invitationSummarySchema,
        }, options),
        adminRevokeInvitation: (organizationId, invitationId, body, options) => call({
            route: ROUTES.revokeInvitation,
            path: PLATFORM_ADMIN_API.revokeInvitation(organizationId, invitationId),
            method: 'POST',
            body,
            schema: invitationSummarySchema,
        }, options),
        adminDomains: (organizationId, options) => call({
            route: ROUTES.domains,
            path: PLATFORM_ADMIN_API.domains(organizationId),
            method: 'GET',
            schema: adminDomainListSchema,
        }, options),
        adminCreateDomain: (organizationId, body, options) => call({
            route: ROUTES.domains,
            path: PLATFORM_ADMIN_API.domains(organizationId),
            method: 'POST',
            body,
            schema: domainCreatedSchema,
        }, options),
        adminDomainAction: (organizationId, domainId, body, options) => call({
            route: ROUTES.domainAction,
            path: PLATFORM_ADMIN_API.domainAction(organizationId, domainId),
            method: 'POST',
            body,
            schema: adminDomainSchema,
        }, options),
        adminSupportAccessGrants: (query, options) => call({
            route: PLATFORM_ADMIN_API.supportAccess,
            path: PLATFORM_ADMIN_API.supportAccess,
            method: 'GET',
            query: { organizationId: query?.organizationId, status: query?.status },
            schema: supportAccessGrantListSchema,
        }, options),
        adminRequestSupportAccess: (body, options) => call({
            route: PLATFORM_ADMIN_API.supportAccess,
            path: PLATFORM_ADMIN_API.supportAccess,
            method: 'POST',
            body,
            schema: supportAccessGrantSchema,
        }, options),
        adminRevokeSupportAccess: (grantId, body, options) => call({
            route: ROUTES.supportAccessRevoke,
            path: PLATFORM_ADMIN_API.supportAccessRevoke(grantId),
            method: 'POST',
            body,
            schema: supportAccessGrantSchema,
        }, options),
        adminAudit: (query, options) => call({
            route: PLATFORM_ADMIN_API.audit,
            path: PLATFORM_ADMIN_API.audit,
            method: 'GET',
            query: {
                organizationId: query?.organizationId,
                action: query?.action,
                actorId: query?.actorId,
                from: query?.from,
                to: query?.to,
                cursor: query?.cursor,
                limit: query?.limit,
            },
            schema: auditPageSchema,
        }, options),
        adminHealth: (query, options) => call({
            route: PLATFORM_ADMIN_API.health,
            path: PLATFORM_ADMIN_API.health,
            method: 'GET',
            query: { range: query?.range, refresh: query?.refresh ? 'true' : undefined },
            schema: adminHealthSchema,
        }, options),
        adminKeys: (options) => call({
            route: PLATFORM_ADMIN_API.keys,
            path: PLATFORM_ADMIN_API.keys,
            method: 'GET',
            schema: adminKeysSchema,
        }, options),
        adminKeysSummary: (options) => call({
            route: PLATFORM_ADMIN_API.keysSummary,
            path: PLATFORM_ADMIN_API.keysSummary,
            method: 'GET',
            schema: adminKeysSummarySchema,
        }, options),
        adminSetKeyExpiry: (project, name, body, options) => call({
            route: ROUTES.keyExpiry,
            path: PLATFORM_ADMIN_API.keyExpiry(project, name),
            method: 'PUT',
            body,
            schema: adminKeySchema,
        }, options),
        adminSetKeyProvider: (project, name, body, options) => call({
            route: ROUTES.keyProvider,
            path: PLATFORM_ADMIN_API.keyProvider(project, name),
            method: 'PUT',
            body,
            schema: adminKeySchema,
        }, options),
        adminSetKeyTracking: (project, name, body, options) => call({
            route: ROUTES.keyTracking,
            path: PLATFORM_ADMIN_API.keyTracking(project, name),
            method: 'PUT',
            body,
            schema: adminKeySchema,
        }, options),
        adminLinkAuth0Organization: (organizationId, body, options) => call({
            route: ROUTES.organizationAuth0,
            path: PLATFORM_ADMIN_API.organizationAuth0(organizationId),
            method: 'POST',
            body,
            schema: auth0OrganizationLinkSchema,
        }, options),
        adminProductSubscriptions: (organizationId, options) => call({
            route: ROUTES.subscriptions,
            path: PLATFORM_ADMIN_API.subscriptions(organizationId),
            method: 'GET',
            schema: adminProductSubscriptionListSchema,
        }, options),
        adminCreateProductSubscription: (organizationId, body, options) => call({
            route: ROUTES.subscriptions,
            path: PLATFORM_ADMIN_API.subscriptions(organizationId),
            method: 'POST',
            body,
            schema: adminProductSubscriptionSchema,
        }, options),
        adminPortals: (organizationId, options) => call({
            route: ROUTES.portals,
            path: PLATFORM_ADMIN_API.portals(organizationId),
            method: 'GET',
            schema: adminPortalListSchema,
        }, options),
        adminCreatePortal: (organizationId, body, options) => call({
            route: ROUTES.portals,
            path: PLATFORM_ADMIN_API.portals(organizationId),
            method: 'POST',
            body,
            schema: adminPortalSchema,
        }, options),
        adminChangePortal: (organizationId, portalId, body, options) => call({
            route: ROUTES.portal,
            path: PLATFORM_ADMIN_API.portal(organizationId, portalId),
            method: 'PATCH',
            body,
            schema: adminPortalSchema,
        }, options),
        adminCustomerAccounts: (organizationId, options) => call({
            route: ROUTES.customerAccounts,
            path: PLATFORM_ADMIN_API.customerAccounts(organizationId),
            method: 'GET',
            schema: adminCustomerAccountListSchema,
        }, options),
        adminCreateCustomerAccount: (organizationId, body, options) => call({
            route: ROUTES.customerAccounts,
            path: PLATFORM_ADMIN_API.customerAccounts(organizationId),
            method: 'POST',
            body,
            schema: adminCustomerAccountSchema,
        }, options),
        adminCreatePortalInvitation: (organizationId, portalId, body, options) => call({
            route: ROUTES.portalInvitations,
            path: PLATFORM_ADMIN_API.portalInvitations(organizationId, portalId),
            method: 'POST',
            body,
            schema: invitationSummarySchema,
        }, options),
        adminChangeCustomerAccount: (organizationId, accountId, body, options) => call({
            route: ROUTES.customerAccount,
            path: PLATFORM_ADMIN_API.customerAccount(organizationId, accountId),
            method: 'PATCH',
            body,
            schema: adminCustomerAccountSchema,
        }, options),
        adminStaff: (options) => call({
            route: PLATFORM_ADMIN_API.staff,
            path: PLATFORM_ADMIN_API.staff,
            method: 'GET',
            schema: staffMemberListSchema,
        }, options),
        adminCreateStaffMember: (body, options) => call({
            route: PLATFORM_ADMIN_API.staff,
            path: PLATFORM_ADMIN_API.staff,
            method: 'POST',
            body,
            schema: staffMemberSchema,
        }, options),
        adminStaffInvitations: (options) => call({
            route: PLATFORM_ADMIN_API.staffInvitations,
            path: PLATFORM_ADMIN_API.staffInvitations,
            method: 'GET',
            schema: staffInvitationListSchema,
        }, options),
        adminCreateStaffInvitation: (body, options) => call({
            route: PLATFORM_ADMIN_API.staffInvitations,
            path: PLATFORM_ADMIN_API.staffInvitations,
            method: 'POST',
            body,
            schema: staffInvitationSchema,
        }, options),
        adminRevokeStaffInvitation: (invitationId, body, options) => call({
            route: ROUTES.revokeStaffInvitation,
            path: PLATFORM_ADMIN_API.revokeStaffInvitation(invitationId),
            method: 'POST',
            body,
            schema: staffInvitationSchema,
        }, options),
        adminChangeStaffMember: (userId, body, options) => call({
            route: ROUTES.staffMember,
            path: PLATFORM_ADMIN_API.staffMember(userId),
            method: 'PATCH',
            body,
            schema: staffMemberSchema,
        }, options),
        adminEndProductSubscription: (organizationId, subscriptionId, body, options) => call({
            route: ROUTES.endSubscription,
            path: PLATFORM_ADMIN_API.endSubscription(organizationId, subscriptionId),
            method: 'POST',
            body,
            schema: adminProductSubscriptionSchema,
        }, options),
        adminCreateOrganization: (body, options) => call({
            route: PLATFORM_API.adminOrganizations,
            path: PLATFORM_API.adminOrganizations,
            method: 'POST',
            body,
            schema: organizationCreatedSchema,
        }, options),
        adminEnterpriseProvisioning: (organizationId, options) => call({
            route: ROUTES.enterpriseProvisioning,
            path: PLATFORM_ADMIN_API.enterpriseProvisioning(organizationId),
            method: 'GET',
            schema: adminEnterpriseProvisioningReadSchema,
        }, options),
        adminRetryEnterpriseProvisioning: (organizationId, body, options) => call({
            route: ROUTES.retryEnterpriseProvisioning,
            path: PLATFORM_ADMIN_API.retryEnterpriseProvisioning(organizationId),
            method: 'POST',
            body,
            schema: adminEnterpriseProvisioningSchema,
        }, options),
        authorizeAction: (body, options) => call({
            route: PLATFORM_ADMIN_API.actionAuthorize,
            path: PLATFORM_ADMIN_API.actionAuthorize,
            method: 'POST',
            body,
            schema: actionAuthorizeResponseSchema,
        }, options),
    };
}
//# sourceMappingURL=admin.js.map